/**
 * The four-frame arc of the free SAT course — the ONE definition of "the film"
 * (spec 010 §2 + amendment 2). The homepage Story Stage plays it today; the
 * course's `See it fill in` (WS-E) and `/what-we-do/sat/about` consume the same
 * exports so the two doors can never show different arcs.
 *
 * Pure data + pure functions. No React, no clock, no randomness: the empty
 * (frame 1) state is prerendered and every value the film shows is derived from
 * the scores a caller passes in (the stage builds those on first play, never at
 * render).
 *
 * Frames:
 *   1  diagnostic  eight dashed empty rings, six ✓/✗ chips arc in
 *   2  re-order    rings tint to bands and re-sort weakest-first
 *   3  practice    the weakest ring fills rose -> amber, "You are here"
 *   4  prove it    that ring fills emerald and flags; the rest cascade to green flags
 */
import { DOMAIN_LABELS, type SatDomain, type SatMasteryBand } from '@/api/sat';
import { DIAGNOSTIC_FACTS } from '@/lib/marketingCtas';
import { bandForPct } from '@/pages/open/openDemoDtos';

/** Each frame is held this long before the next begins (owner: slower). */
export const FRAME_HOLD_MS = 5000;
/** Duration of a frame-to-frame swap (crossfade / FLIP re-sort). */
export const TRANSITION_MS = 600;
/** Long ring fills (frames 3 and 4). */
export const LONG_FILL_MS = 2500;
/** Short ring fill used for tints and the frame-4 cascade. */
export const TINT_FILL_MS = TRANSITION_MS;
/** Delay between rings tinting on frame 2. */
export const TINT_STAGGER_MS = 120;

/** Frame 1: six chips arc in from the caption over this long. */
export const CHIPS = {
  /** ok=true renders ✓, false renders ✗ */
  pattern: [true, true, false, true, false, true] as readonly boolean[],
  totalMs: 2000,
  flightMs: 650,
  /** stagger so the last chip lands exactly at totalMs */
  staggerMs: (2000 - 650) / 5,
} as const;

/** Frame 4 flag: scale .6 -> 1 + opacity, 200ms, emerald pin. */
export const FLAG = {
  popMs: 200,
  fromScale: 0.6,
  easing: 'cubic-bezier(.2,.8,.2,1)',
  /** delay between the first flag and the second ring's flag, and between each after */
  staggerMs: 400,
} as const;

/** The weakest ring climbs to here on frame 3 (85 is the mastery-check bar; this is "moving"). */
export const PRACTICE_TARGET_PCT = 68;

export type FrameNumber = 1 | 2 | 3 | 4;

export interface StoryFrame {
  n: FrameNumber;
  /** Static caption, <= 60 chars. */
  caption: string;
  /** Two-line caption for `/what-we-do/sat/about` (spec 010 §5.4). */
  longCaption: string;
  holdMs: number;
  transitionMs: number;
}

export const STORY_FRAMES: readonly StoryFrame[] = [
  {
    n: 1,
    caption: `Start with a ${DIAGNOSTIC_FACTS.questions}-question diagnostic.`,
    longCaption: `Start with a ${DIAGNOSTIC_FACTS.questions}-question diagnostic. It takes ${DIAGNOSTIC_FACTS.minutesLabel}, needs no account, and touches all eight areas.`,
    holdMs: FRAME_HOLD_MS,
    transitionMs: TRANSITION_MS,
  },
  {
    n: 2,
    caption: 'Your course re-orders, weakest area first.',
    longCaption: 'Your course re-orders, weakest area first. Each area is coloured by how you did, and the course starts where you need it most.',
    holdMs: FRAME_HOLD_MS,
    transitionMs: TRANSITION_MS,
  },
  {
    n: 3,
    caption: 'Practice aims at that area until it moves.',
    longCaption: 'Practice aims at that area until it moves. Every set is picked for your weakest area, and you can watch it climb.',
    holdMs: FRAME_HOLD_MS,
    transitionMs: TRANSITION_MS,
  },
  {
    n: 4,
    caption: 'Prove it, and it turns green. Then the next.',
    longCaption: 'Prove it, and it turns green. An area is only marked done once you have shown it, then the course moves on to the next.',
    holdMs: FRAME_HOLD_MS,
    transitionMs: TRANSITION_MS,
  },
] as const;

/**
 * The hero window's status row: one line in the course's own next-step voice.
 * Frames 3-4 name areas, which the caller derives from the demo scores
 * (weakest and second-weakest), so nothing here is literal.
 */
/**
 * `settled` = frame 4 has finished flagging every ring. The cascade line
 * ("X proven · next: Y") would then contradict the eight green rings above it.
 */
export function storyStatus(
  frame: FrameNumber,
  names: { weakest: string; second: string },
  settled = false,
): string {
  switch (frame) {
    case 1:
      return `Next: the diagnostic · ${DIAGNOSTIC_FACTS.questions} questions · ${DIAGNOSTIC_FACTS.minutesLabel}`;
    case 2:
      return 'Diagnostic done · 8 areas mapped, weakest first';
    case 3:
      return `Practice is ready: ${names.weakest}`;
    default:
      return settled ? 'All 8 areas proven' : `${names.weakest} proven · next: ${names.second}`;
  }
}

/** Line under the stage on the homepage (spec 010 §2). */
export const STORY_UNDER_LINE = 'This is an example student. Yours is built from your own answers.';

/**
 * The delay (from frame-4 start) at which the ring in sorted position `pos`
 * (0 = weakest) shows its flag. The first flag lands when its ring's long fill
 * finishes; each other ring then flags in at FLAG.staggerMs intervals.
 */
export function flagDelayMs(pos: number): number {
  if (pos <= 0) return LONG_FILL_MS;
  return LONG_FILL_MS + FLAG.popMs + FLAG.staggerMs * (pos - 1) + 200;
}

/** Time from frame-4 start until the eighth flag has finished popping. */
export function frame4SettledMs(ringCount = 8): number {
  return flagDelayMs(ringCount - 1) + FLAG.popMs;
}

/**
 * Time from play start until frame 4 is fully settled (8 green rings, 8 flags).
 * Frames 1-3 hold FRAME_HOLD_MS each. The spec acceptance is <= 21s.
 */
export function storyTotalMs(ringCount = 8): number {
  return FRAME_HOLD_MS * 3 + frame4SettledMs(ringCount);
}

export interface RingFill {
  ms: number;
  delayMs: number;
}

export interface RingState {
  domain: SatDomain;
  /** null = empty (unearned, dashed). */
  pct: number | null;
  band: SatMasteryBand;
  /** Sorted position (0 = weakest) once re-ordered; the original index on frame 1. */
  order: number;
  /** The proven, green state (frame 4). */
  mastered: boolean;
  /** Flag delay in ms from frame start; null = no flag. */
  flagDelayMs: number | null;
  youAreHere: boolean;
  fill: RingFill | 'instant';
}

/**
 * Every ring's state for a frame. `domains` is the course's own order;
 * `scores` is the diagnostic outcome (e.g. from `demoDomainMap()`), or null
 * before the film first plays — in which case every frame reads as frame 1.
 */
export function ringStatesForFrame(
  frame: FrameNumber,
  domains: readonly SatDomain[],
  scores: Readonly<Record<string, number>> | null,
): RingState[] {
  const sorted = scores
    ? [...domains].sort((a, b) => (scores[a] ?? 0) - (scores[b] ?? 0))
    : [...domains];

  return domains.map((domain, i) => {
    const score = scores ? (scores[domain] ?? 0) : null;
    const pos = sorted.indexOf(domain);
    const empty: RingState = {
      domain,
      pct: null,
      band: 'NOT_STARTED',
      order: i,
      mastered: false,
      flagDelayMs: null,
      youAreHere: false,
      fill: 'instant',
    };
    if (frame === 1 || score == null) return empty;

    const tint: RingState = {
      ...empty,
      pct: score,
      band: bandForPct(score),
      order: pos,
      // Frame 2 tints in the course's own order; on later frames the arc is already there.
      fill: { ms: TINT_FILL_MS, delayMs: TINT_STAGGER_MS * i },
    };
    if (frame === 2) return tint;

    if (frame === 3) {
      if (pos !== 0) return tint;
      return {
        ...tint,
        pct: PRACTICE_TARGET_PCT,
        band: bandForPct(PRACTICE_TARGET_PCT),
        youAreHere: true,
        fill: { ms: LONG_FILL_MS, delayMs: 0 },
      };
    }

    // Frame 4: the weakest fills to green over the long fill, then flags; the rest cascade.
    const flag = flagDelayMs(pos);
    return {
      ...tint,
      pct: 100,
      band: 'PROFICIENT',
      mastered: true,
      flagDelayMs: flag,
      fill: pos === 0 ? { ms: LONG_FILL_MS, delayMs: 0 } : { ms: TINT_FILL_MS, delayMs: Math.max(0, flag - TINT_FILL_MS) },
    };
  });
}

// ── Homepage v3: "tap the area you'd start with" (HOME-V3 §A) ─────────────────

/** Skills per area in the bank (VERIFIED 2026-10-06, `SELECT domain, count(*) FROM sat_skills`). */
export const DOMAIN_SKILL_COUNTS: Readonly<Record<SatDomain, number>> = {
  INFORMATION_AND_IDEAS: 6,
  CRAFT_AND_STRUCTURE: 7,
  EXPRESSION_OF_IDEAS: 6,
  STANDARD_ENGLISH_CONVENTIONS: 8,
  ALGEBRA: 7,
  ADVANCED_MATH: 8,
  PROBLEM_SOLVING_AND_DATA_ANALYSIS: 7,
  GEOMETRY_AND_TRIGONOMETRY: 7,
};

/** Hover line at rest: `{Full label} · {n} skills`. */
export function hoverLine(domain: SatDomain): string {
  return `${DOMAIN_LABELS[domain]} · ${DOMAIN_SKILL_COUNTS[domain]} skills`;
}

export const pickCaption = (short: string) => `If ${short} is weakest, your course re-orders.`;

export const pickUnderLine = (full: string) =>
  `Example student, with ${full} as the weakest area. The diagnostic finds yours for real.`;

/**
 * The visitor's guess: swap the picked area's score with the current minimum. Picking the
 * current weakest is identity; otherwise exactly two entries change and the multiset of
 * scores is preserved, so the film's sort, climb and prove verbs all still derive from it.
 * Pure: returns a new record.
 */
export function withWeakest(scores: Readonly<Record<string, number>>, domain: string): Record<string, number> {
  const out = { ...scores };
  if (!(domain in out)) return out;
  let min: string | null = null;
  for (const [d, v] of Object.entries(out)) if (min === null || v < out[min]) min = d;
  if (min === null || min === domain) return out;
  [out[min], out[domain]] = [out[domain], out[min]];
  return out;
}
